import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { Loader2, Lock, Shield, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

const ForcePasswordChangePage = () => {
  const { user, setNewPassword, logout } = useAuth();
  const navigate = useNavigate();
  
  const [newPassword, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password validation rules
  const validations = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
    passwordsMatch: newPassword === confirmPassword && newPassword.length > 0
  };

  const isValid = Object.values(validations).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Please meet all password requirements');
      return;
    }

    setLoading(true);
    const result = await setNewPassword(newPassword);
    setLoading(false);

    if (result.success) {
      toast.success('Password changed successfully! Welcome to Harmony Apps.');
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const ValidationItem = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-sm ${valid ? 'text-green-600' : 'text-muted-foreground'}`}>
      {valid ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : (
        <XCircle className="w-4 h-4 text-muted-foreground/50" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="force-password-change-card">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <CardTitle className="text-2xl">Set Your New Password</CardTitle>
            <CardDescription className="mt-2">
              Welcome, <strong>{user?.full_name || user?.username}</strong>! 
              For security reasons, you must set a new password before continuing.
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPasswordValue(e.target.value)}
                    placeholder="Enter new password"
                    className="pl-10 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="pl-10"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Password Requirements */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-muted-foreground mb-2">Password Requirements:</p>
                <ValidationItem valid={validations.minLength} text="At least 8 characters" />
                <ValidationItem valid={validations.hasUppercase} text="One uppercase letter" />
                <ValidationItem valid={validations.hasLowercase} text="One lowercase letter" />
                <ValidationItem valid={validations.hasNumber} text="One number" />
                <ValidationItem valid={validations.passwordsMatch} text="Passwords match" />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !isValid}
              data-testid="set-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting Password...
                </>
              ) : (
                'Set Password & Continue'
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={logout}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Sign out and use a different account
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChangePage;
